import HeaderTable from "./components/HeaderTable";
import { formatMoney } from "infra/helpers/number";
import {
  FiltroRelatorioBalancoFinanceiro,
  BalancoFinanceiroResponse,
} from "features/relatorios/typings";
import CardDefault from "components/Cards/CardDefault";
import RelatorioSectionTitle from "ui/components/Relatorio/RelatorioSectionTitle";
import RelatorioItem from "./components/RelatorioItem";
import { OperacaoEnum } from "resources/enums/conta";
import colors from "resources/theme/colors";
import { Empty } from "antd";
import { useLoading } from "components/GlobalLoading";
import { Text, Flex, Divider, Box } from "@chakra-ui/react";
import { LinhaTituloValor } from "./components/LinhaTituloValor";
import { SectionTitle } from "./components/SectionTitle";
import { TableColumns } from "./components/TableColumns";
import RelatorioReceitaItem from "./components/RelatorioReceitaItem";
import { flattenRelatorioCategoriasTree } from "core/features/relatorios/utils/flattenRelatorioCategoriasTree";
import { BalancoFinanceiroReceitaResponse } from "core/features/relatorios/typings";

interface RelatorioBalancoFinanceiroProps {
  onChangeFiltro: (v: Partial<FiltroRelatorioBalancoFinanceiro>) => void;
  filtro: FiltroRelatorioBalancoFinanceiro;
  data?: BalancoFinanceiroResponse;
  isLoading?: boolean;
  setItemDetalhamentoContaCategoria?: (
    item: BalancoFinanceiroReceitaResponse
  ) => void;
}

function RelatorioBalancoFinanceiro({
  filtro,
  data,
  isLoading,
  onChangeFiltro,
  setItemDetalhamentoContaCategoria,
}: RelatorioBalancoFinanceiroProps) {
  const flattenCategoriasReceita = flattenRelatorioCategoriasTree(
    data?.receita.categorias || []
  );

  useLoading(!!isLoading);

  return (
    <CardDefault>
      <HeaderTable
        value={filtro.search || ""}
        onSearch={(v) => onChangeFiltro({ search: v })}
        onSelectOperacao={(operacao) => onChangeFiltro({ operacao })}
        operacao={filtro.operacao}
        filtro={filtro}
        data={data}
      />

      {filtro.operacao === OperacaoEnum.Receita || !filtro.operacao ? (
        <>
          <SectionTitle title="Histórico de Receitas" marginTop />

          {flattenCategoriasReceita.length ? (
            <RelatorioSectionTitle
              containerProps={{ mt: 4, mb: 6 }}
              items={[
                {
                  text: "Descrição",
                  colSpan: 16,
                  bold: true,
                },
                {
                  text: "Competência",
                  colSpan: 4,
                  bold: true,
                  textAlign: "center",
                },
                {
                  text: "Valor",
                  colSpan: 4,
                  textAlign: "right",
                  bold: true,
                },
              ]}
            />
          ) : null}

          {flattenCategoriasReceita.map((categoria, index) => (
            <Box key={categoria.id}>
              <RelatorioReceitaItem
                key={`${categoria.nome}${index}${categoria.mesCompetencia}`}
                item={categoria}
                index={index}
                indent={categoria.nivel}
                startBg="white"
                onClick={
                  setItemDetalhamentoContaCategoria
                    ? () => setItemDetalhamentoContaCategoria?.(categoria)
                    : undefined
                }
              />
            </Box>
          ))}

          {data?.totalReceita ? (
            <Flex
              backgroundColor={colors.black100}
              justifyContent="flex-end"
              fontWeight="bold"
            >
              <Text mr="64px">TOTAL DE RECEITAS:</Text>
              <Text>{formatMoney(data?.totalReceita)}</Text>
            </Flex>
          ) : null}

          {!isLoading && !data?.receita.categorias.length ? (
            <>
              <Empty description="Sem histórico de receitas para o filtro atual" />
              <Divider mt={2} />
            </>
          ) : null}
        </>
      ) : null}

      {filtro.operacao === OperacaoEnum.Despesa || !filtro.operacao ? (
        <>
          <SectionTitle title="Histórico de Despesas" />

          {data?.despesa?.categorias?.length ? <TableColumns /> : null}

          {data?.despesa?.categorias.map((categoria) => (
            <Box key={categoria.id} mb={4}>
              {categoria?.categoriaFinanceiraPai ? (
                <Text style={{ fontWeight: "bold" }}>
                  {categoria.categoriaFinanceiraPai.breadcrumb}{" "}
                  {categoria.categoriaFinanceiraPai.nome}
                </Text>
              ) : null}
              <RelatorioItem key={categoria.id} item={categoria} />

              <RelatorioSectionTitle
                containerProps={{ mt: 1, mb: 2 }}
                items={[
                  {
                    text: `Total`,
                    colSpan: 22,
                    bold: true,
                    color: "black",
                  },
                  {
                    text: `${formatMoney(categoria.total)}`,
                    colSpan: 2,
                    bold: true,
                    textAlign: "right",
                    color: "black",
                  },
                ]}
              />
            </Box>
          ))}

          {data?.totalDespesa ? (
            <Flex
              backgroundColor={colors.black100}
              justifyContent="flex-end"
              fontWeight="bold"
            >
              <Text mr="64px">TOTAL DE DESPESAS:</Text>
              <Text>{formatMoney(data?.totalDespesa)}</Text>
            </Flex>
          ) : null}

          {!isLoading && !data?.despesa.categorias.length ? (
            <>
              <Empty description="Sem histórico de despesas para o filtro atual" />
              <Divider mt={2} />
            </>
          ) : null}
        </>
      ) : null}

      {data ? (
        <>
          <SectionTitle title="RESUMO" />

          <Flex
            mt={2}
            flexDir="column"
            p={2}
            borderWidth={1}
            borderColor={colors.gray50}
          >
            <LinhaTituloValor
              title="Saldo Anterior:"
              value={data?.totalSaldoAnterior}
            />
            <LinhaTituloValor
              title="Total de Receitas:"
              value={data?.totalReceita}
            />
            <LinhaTituloValor
              title="Total de Despesas:"
              value={data?.totalDespesa}
            />

            <LinhaTituloValor
              title="Saldo do Período:"
              value={data?.saldoPeriodo}
            />

            <Flex
              justifyContent="space-between"
              gap={4}
              w="100%"
              borderTopWidth={1}
              borderTopColor={colors.gray50}
              mt={2}
            >
              <Text fontWeight="bold">Resultado:</Text>

              <Text fontWeight="bold">{formatMoney(data.resultado)}</Text>
            </Flex>
          </Flex>
        </>
      ) : null}

      {data?.contasBancarias?.length ? (
        <>
          <SectionTitle title="CONTAS BANCÁRIAS" />

          {data?.contasBancarias.map((contaBancaria) => (
            <Flex
              mt={2}
              key={contaBancaria.id}
              flexDir="column"
              p={2}
              borderWidth={1}
              borderColor={colors.gray50}
            >
              <Text
                color={colors.black}
                display="inline"
                fontWeight="bold"
                mb={2}
              >
                {contaBancaria.nome.toUpperCase()}
              </Text>

              <LinhaTituloValor
                title="01 - Saldo Anterior"
                value={contaBancaria.saldoAnterior}
              />
              <LinhaTituloValor
                title="02 - Créditos"
                value={contaBancaria.creditos}
              />
              <LinhaTituloValor
                title="03 - Débitos"
                value={contaBancaria.debitos}
              />

              {contaBancaria.transferenciaSaida ? (
                <LinhaTituloValor
                  title="04 - Transferência (-)"
                  value={contaBancaria.transferenciaSaida}
                />
              ) : null}
              {contaBancaria.transferenciaEntrada ? (
                <LinhaTituloValor
                  title="04 - Transferência (+)"
                  value={contaBancaria.transferenciaEntrada}
                />
              ) : null}
              <Flex
                justifyContent="space-between"
                gap={4}
                w="100%"
                borderTopWidth={1}
                borderTopColor={colors.gray50}
                mt={2}
              >
                <Text ml="auto" fontWeight="bold">
                  {formatMoney(contaBancaria.total)}
                </Text>
              </Flex>
            </Flex>
          ))}
        </>
      ) : null}
    </CardDefault>
  );
}

export default RelatorioBalancoFinanceiro;
